import { DataTable } from '@/components/features/shared/DataTable'
import { Badge } from '@/components/ui/badge'
import { IconButton } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { PaginatedResponse } from '@/domain'
import { Account, formatAccountStatus, getAccountStatusColor } from '@/domain/accounts/account'
import { ColumnDef, PaginationState } from '@tanstack/react-table'
import { ArrowRight } from 'lucide-react'
import React from 'react'
import { RenderBalanceInColor } from './account-utils'
import { renderDatetimeInline } from '@/components/features/shared/utils'

interface AccountsTableProps {
  paginated: PaginatedResponse<Account> | undefined
  isLoading?: boolean
  pagination?: PaginationState
  onPaginationChange?: React.Dispatch<React.SetStateAction<PaginationState>>
  onRowClick?: (account: Account) => void
}

export const AccountsTable = ({
  paginated,
  isLoading,
  pagination,
  onPaginationChange,
  onRowClick,
}: AccountsTableProps) => {
  const data = paginated?.data || []

  const columns: ColumnDef<Account>[] = [
    {
      accessorKey: 'userName',
      header: 'Cliente',
      cell: ({ row }) => {
        const account = row.original
        return (
          <div className="flex flex-col">
            <span className="font-medium">{account.userName}</span>
            <span className="text-sm text-muted-foreground">
              Cuenta: {account.accountNumber}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: 'currentBalance',
      header: 'Saldo',
      cell: ({ row }) => {
        const balance = row.original.currentBalance
        const isNegative = balance < 0
        return (
          <span className={`font-medium ${isNegative ? 'text-red-500' : 'text-green-600'}`}>
            <RenderBalanceInColor balance={balance || 0} />
          </span>
        )
      },
    },
    {
      accessorKey: 'lastMovementDate',
      header: 'Último Movimiento',
      cell: ({ row }) => {
        return renderDatetimeInline(row.original.lastMovementDate)
      },
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: ({ row }) => {
        const account = row.original
        return (
          <Badge className={getAccountStatusColor(account.status)}>
            {formatAccountStatus(account.status)}
          </Badge>
        )
      },
    },
    {
      id: 'actions',
      header: 'Acciones',
      cell: ({ row }) => {
        const account = row.original
        return (
          <div className="flex items-center gap-2">
            <IconButton variant="default">
              <ArrowRight className="h-3.5 w-3.5" />
            </IconButton>
          </div>
        )
      },
    },
  ]

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    )
  }

  return (
    <DataTable
      columns={columns}
      data={data}
      pageCount={paginated?.totalPages}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      totalRecords={paginated?.total}
      onRowClick={onRowClick}
    />
  )
}